<template>
    <div
        class="cart"
        :class="{'cart-collapsed': isCollapsed, 'cart-expanded': !isCollapsed, 'cart-minified': minified}"
        @click.self="toggleCart"
    >
        <div class="cart-header" @click.stop="toggleCart">
            <div class="container">
                <strong>{{ $t("cart.title") }}</strong>
                <span class="expand-icon">
                    <i class="fa" :class="{'fa-angle-up': !isCollapsed, 'fa-angle-down': isCollapsed}"></i>
                </span>
            </div>
        </div>
        <div class="cart-content" v-if="!isCollapsed">
            <div class="container">
                <span class="empty" v-if="!booking">{{ $t("cart.empty") }}</span>
                <div v-if="booking && product">
                    <div class="cart-overview">
                        <div class="cart-details">
                            <strong>{{ product.title }}</strong>
                            <div v-if="product.options.audiences">
                                <ul>
                                    <li v-for="audience in selectedAudiences" :key="'audience-' + audience.id">
                                        <span>{{ audience.title }}</span>
                                        {{ audience.quantity }}
                                    </li>
                                    <li v-for="upsell in selectedUpsells" :key="'audience-upsell-' + upsell.id">
                                        <span>{{ upsell.title }}</span>
                                        {{ upsell.quantity }}
                                    </li>
                                    <li v-if="!minified && plot">
                                        <span>{{ $t("cart.items.date") }}</span>
                                        {{ date_format(plot.date) }}
                                    </li>
                                    <li v-if="!minified && plot && product.options.timeslots">
                                        <span>{{ $t("cart.items.time") }}</span>
                                        {{ plot.time.start }} - {{ plot.time.end }}
                                    </li>
                                </ul>
                            </div>
                            <div v-else>
                                <ul>
                                    <li>
                                        <span>{{ $t("cart.items.tickets") }}</span>
                                        {{ booking.product.quantity }}
                                    </li>
                                    <li v-for="upsell in selectedUpsells" :key="'ticket-upsell-' + upsell.id">
                                        <span>{{ upsell.title }}</span>
                                        {{ upsell.quantity }}
                                    </li>
                                    <li v-if="!minified && plot">
                                        <span>{{ $t("cart.items.date") }}</span>
                                        {{ date_format(plot.date) }}
                                    </li>
                                    <li v-if="!minified && plot && product.options.timeslots">
                                        <span>{{ $t("cart.items.time") }}</span>
                                        {{ plot.time.start }} - {{ plot.time.end }}
                                    </li>
                                </ul>
                            </div>
                            <span class="total">{{ $t("cart.total") }}: &euro;{{ total_rounded }}</span>
                        </div>
                        <div class="cart-image">
                            <div class="image" :style="image">&nbsp;</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="cart-buttons" v-if="!minified">
                <div class="cart-notice" v-if="total === 0">
                    <div class="container">
                        <div class="alert alert-secondary">
                            <i class="fa fa-fw fa-exclamation-triangle"></i>
                            <span
                                class="ml-2">&nbsp;Het is niet mogelijk om een gratis ticket te boeken bij deze kiosk!</span>
                        </div>
                    </div>
                </div>
                <div class="container">
                    <div class="row">
                        <div class="col-md-6 text-left">
                            <button class="cancel" @click.prevent="cancel()">
                                <i class="fa fa-times align-middle mr-1" />
                                {{ $t("cart.buttons.cancel") }}
                            </button>
                        </div>
                        <div class="col-md-6 text-right">
                            <button class="checkout" @click.prevent="checkout()" :class="{'disabled' : (total === 0)}">
                                {{ $t("cart.buttons.checkout") }}
                                <i class="fa fa-angle-right align-middle ml-1" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.cart {

    position: fixed;
    background: #fff;
    animation: slideInCart 0.25s linear;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 800px;
    z-index: 10;
    transition: height 0.3s ease;

    &.cart-collapsed {
        height: 64px;
        max-height: 64px;
        overflow: hidden;
        background: rgba(var(--color-cart-background), 1);

        .cart-header {
            cursor: pointer;
            border-bottom: none;
        }

        .cart-content {
            display: none;
        }
    }

    &.cart-expanded {
        max-height: 800px;
        overflow-y: auto;
    }

    &.cart-expanded.cart-minified {
        height: 300px;
    }

    @media (max-width: 1024px) {
        &.cart-expanded:not(.cart-minified):after {
            position: fixed;
            content: ' ';
            top: 0;
            left: 0;
            background: linear-gradient(180deg, rgba(0, 0, 0, .7), rgba(0, 0, 0, .8));
            width: 100vw;
            height: calc(100vh - 800px);
            z-index: -1;
        }
    }

    .cart-header {
        background: rgba(var(--color-cart-background), 1);
        border-bottom: 3px solid rgba(0, 0, 0, 0.25);
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12.5px 20px;

        strong {
            color: #fff;
            font-size: 24px;
            text-transform: uppercase;
            margin: 0;
        }

        .expand-icon {
            margin-left: 10px;
            color: #fff;
            font-size: 20px;
            cursor: pointer;
        }
    }

    .cart-content {

        position: relative;
        height: 100%;
        padding: 50px 0;

        .empty {

            color: rgba(var(--color-cart-text), 1);
            display: block;
            width: 100%;
            margin: 50px 0;
            text-align: center;
            font-size: 20px;

        }

        .cart-overview {

            width: 100%;

            .cart-image {

                @media (max-width: 1024px) {
                    display: none;
                }

                float: right;
                width: 150px;

                .image {

                    width: 150px;
                    height: 150px;
                    border-radius: 10px;

                }

            }

            .cart-details {

                float: left;
                width: calc(100% - 200px);

                strong {

                    font-size: 24px;
                    display: block;
                    margin-bottom: 15px;
                    color: rgba(var(--color-cart-text), 1);

                }

                ul {

                    list-style: none;
                    margin: 0;
                    padding: 0;

                    li {

                        display: block;
                        margin-bottom: 10px;
                        font-weight: 600;
                        font-size: 20px;
                        color: rgba(var(--color-cart-text), 1);

                        span {

                            display: block;
                            font-weight: 400;
                            color: rgba(var(--color-cart-text), 1);

                        }

                    }

                }

                .total {

                    display: block;
                    font-size: 28px;
                    border-top: 1px solid rgba(var(--color-cart-background), 0.35);
                    margin-top: 25px;
                    padding-top: 25px;

                }

            }

        }

        .cart-buttons {

            position: fixed;
            background: rgba(var(--color-cart-background), 0.1);
            bottom: 0;
            left: 0;
            width: 100%;
            padding: 30px 0;

            .cart-notice {

                position: absolute;
                top: -100px;
                left: 0;
                width: 100%;

                .alert {
                    font-size: 22px;
                }

            }

            button {

                border: none;
                border-radius: 100px;
                min-width: 300px;
                outline: none;
                padding: 10px 25px;
                font-size: 24px;

                &.disabled {
                    opacity: 0.5;
                }

            }

            .checkout {
                background: rgba(var(--color-button-background), 1);
                color: rgba(var(--color-button-text), 1);
                border-bottom: 3px solid rgba(0, 0, 0, 0.25);
            }

            .cancel {
                color: #de3023;
                background: transparent;
                min-width: unset;
                padding: 10px 0;
            }

        }

    }

    @media (max-width: 1024px) {

        &.c {
            height: 600px;
        }
    }
}

@keyframes slideInCart {
    0% {
        transform: translateY(200px);
    }
    100% {
        transform: translateY(0);
    }
}
</style>
<script>
import { mapGetters } from "vuex";

import { BOOKING_RESET, DISPLAY_UPDATE } from "@/store/mutations.type";

export default {
    name: "Cart",
    data() {
        return {
            isCollapsed: true,
            minified: true,
            booking: null,
            product: null,
            image: null,
            plot: null,
            total: 0,
            total_rounded: 0.00
        };
    },
    methods: {
        toggleCart() {
            this.isCollapsed = !this.isCollapsed;
        },
        calculate(round = false) {
            let price = 0;

            if (this.booking !== null) {

                if (this.product.options.audiences) {
                    Object.keys(this.booking.audiences).forEach((id) => {
                        let item = this.booking.audiences[parseInt(id, 10)];
                        let quantity = item.quantity;
                        let audience = Array.isArray(this.product.audiences)
                            ? this.product.audiences.find((entry) => entry.id === item.id)
                            : null;
                        let audiencePrice = audience ? audience.price : 0;

                        if (this.product.types.price === "AUDIENCE") {
                            price += (audiencePrice * quantity);
                        } else if (this.product.types.price === "FIXED") {
                            price += (this.product.price * quantity);
                        } else {
                            let plot = this.getPlot(this.booking.product.plot);
                            if (plot) {
                                price += (plot.price * quantity);
                            }
                        }

                    });

                } else {

                    if (this.product.types.price === "FIXED") {
                        price += (this.product.price * this.getQuantity);
                    } else {
                        let plot = this.getPlot(this.booking.product.plot);
                        if (plot) {
                            price += (plot.price * this.getQuantity);
                        }
                    }

                }

            }

            if (this.booking !== null && Array.isArray(this.booking.upsells)) {
                this.booking.upsells.forEach((upsell) => {
                    price += (parseFloat(upsell.price) * parseInt(upsell.quantity));
                });
            }
            return (round ? this.round(price) : price);
        },
        checkout() {
            if (this.calculate() > 0 && !this.getActs) {
                this.$router.push({
                    name: "checkout"
                });
            } else if (this.calculate() > 0) {
                this.$router.push({
                    name: "confirm"
                });
                this.isCollapsed = true;
            }
        },
        cancel() {
            this.$store.commit(BOOKING_RESET);
            this.$store.commit(DISPLAY_UPDATE, {
                component: "cart",
                view: false
            });
            this.isCollapsed = true;
            this.$router.push({
                name: "catalog"
            });
        }
    },
    watch: {
        getBooking: {
            deep: true,
            handler(booking) {
                this.booking = booking;

                if (booking.product.id !== null) {

                    this.product = this.getProduct(booking.product.id);

                    if (this.product.image) {
                        this.image = "background-image: url(\"" + this.cdn(this.product.image) + "\")";
                    }

                    if (booking.product.plot) {
                        this.plot = this.getPlot(booking.product.plot);
                    }

                } else {
                    this.booking = null;
                }

                this.total = this.calculate();
                this.total_rounded = this.calculate(true);
            }
        },
        getDisplay: {
            deep: true,
            handler(display) {
                this.minified = !display.cart;
                this.isCollapsed = !display.cart;
            }
        }
    },
    computed: {
        ...mapGetters([
            "getBooking",
            "getProduct",
            "getPlot",
            "getQuantity",
            "getDisplay",
            "getActs"
        ]),
        selectedAudiences() {
            if (!this.booking || !Array.isArray(this.booking.audiences) || !this.product) {
                return [];
            }

            return this.booking.audiences
                .filter((audience) => audience && audience.quantity > 0)
                .map((audience) => {
                    const productAudience = Array.isArray(this.product.audiences)
                        ? this.product.audiences.find((item) => item.id === audience.id)
                        : null;

                    return {
                        id: audience.id,
                        quantity: audience.quantity,
                        title: productAudience ? productAudience.name : `#${audience.id}`
                    };
                });
        },
        selectedUpsells() {
            if (!this.booking || !Array.isArray(this.booking.upsells) || !this.product) {
                return [];
            }

            return this.booking.upsells
                .filter((upsell) => upsell && upsell.quantity > 0)
                .map((upsell) => {
                    const productUpsell = Array.isArray(this.product.upsells)
                        ? this.product.upsells.find((item) => item.id === upsell.id)
                        : null;

                    return {
                        id: upsell.id,
                        quantity: upsell.quantity,
                        title: productUpsell ? productUpsell.title : `#${upsell.id}`
                    };
                });
        }
    }
};
</script>
